import React, { useState } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';

const SpeakersContainer = styled.section`
  padding: 6rem 0;
  background: ${({ theme }) => theme.colors.background.primary};
  position: relative;
  overflow: hidden;
`;

const AnimatedBackground = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 0;
`;

const FloatingElement = styled(motion.div)`
  position: absolute;
  width: ${({ size }) => size}px;
  height: ${({ size }) => size}px;
  border-radius: 50%;
  background: ${({ color, theme }) => color || theme.colors.primary.blue};
  opacity: 0.1;
  filter: blur(2px);
`;

const NetworkNode = styled(motion.div)`
  position: absolute;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.primary.cyan};
  opacity: 0.6;
  
  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: 100px;
    height: 1px;
    background: linear-gradient(90deg, ${({ theme }) => theme.colors.primary.cyan}60, transparent);
    transform: translate(-50%, -50%) rotate(${({ rotation }) => rotation}deg);
    animation: pulse 3s ease-in-out infinite;
  }
  
  @keyframes pulse {
    0%, 100% { opacity: 0.3; }
    50% { opacity: 0.8; }
  }
`;

const CodeParticle = styled(motion.div)`
  position: absolute;
  font-family: ${({ theme }) => theme.fonts.mono};
  font-size: 12px;
  color: ${({ theme }) => theme.colors.primary.yellow};
  opacity: 0.3;
  pointer-events: none;
`;

const SpeakersHeader = styled.div`
  text-align: center;
  margin-bottom: 4rem;
  position: relative;
  z-index: 2;
`;

const SpeakersTitle = styled(motion.h2)`
  font-size: 3rem;
  margin-bottom: 1rem;
  background: ${({ theme }) => theme.gradients.primary};
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  font-weight: 800;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 2.5rem;
  }
`;

const SpeakersSubtitle = styled.p`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 1.1rem;
  max-width: 600px;
  margin: 0 auto 2rem;
`;

const AnnouncementBanner = styled(motion.div)`
  background: ${({ theme }) => theme.gradients.cyber};
  padding: 1rem 2rem;
  border-radius: 15px;
  border: 2px solid ${({ theme }) => theme.colors.primary.yellow};
  max-width: 500px;
  margin: 0 auto;
  text-align: center;
  box-shadow: 0 0 30px rgba(255, 229, 0, 0.3);
`;

const DayTabs = styled.div`
  display: flex;
  justify-content: center;
  gap: 1rem;
  margin-bottom: 3rem;
  position: relative;
  z-index: 2;
`;

const DayTab = styled(motion.button)`
  padding: 1rem 2rem;
  background: ${({ active, theme }) => active ? theme.colors.primary.blue : theme.colors.surface};
  border: 2px solid ${({ active, theme }) => active ? theme.colors.primary.blue : theme.colors.border};
  border-radius: 25px;
  color: ${({ active, theme }) => active ? theme.colors.text.primary : theme.colors.text.secondary};
  cursor: pointer;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
  transition: all 0.3s ease;
  
  &:hover {
    border-color: ${({ theme }) => theme.colors.primary.blue};
    background: ${({ theme }) => theme.colors.primary.blue};
    color: ${({ theme }) => theme.colors.text.primary};
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: 0.75rem 1.5rem;
    font-size: 0.875rem;
  }
`;

const SpeakersContent = styled.div`
  position: relative;
  z-index: 2;
`;

const SpeakersGrid = styled(motion.div)`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 2rem;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
  }
`;

const SpeakerCard = styled(motion.div)`
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 20px;
  overflow: hidden;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
  
  &:hover {
    transform: translateY(-10px);
    border-color: ${({ theme }) => theme.colors.primary.yellow};
    box-shadow: 0 20px 40px rgba(255, 229, 0, 0.1);
  }
`;

const SpeakerImage = styled.div`
  width: 100%;
  height: 200px;
  background: ${({ theme, hasImage }) => hasImage ? '#f0f0f0' : theme.gradients.cyber};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 3rem;
  color: ${({ theme }) => theme.colors.text.primary};
  position: relative;
  overflow: hidden;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(45deg, transparent 30%, rgba(255, 255, 255, 0.1) 50%, transparent 70%);
    animation: speakerShine 3s ease-in-out infinite;
  }
  
  @keyframes speakerShine {
    0% { transform: translateX(-100%); }
    100% { transform: translateX(100%); }
  }
`;

const SpeakerInfo = styled.div`
  padding: 1.25rem;
`;

const SpeakerName = styled.h3`
  font-size: 1.1rem;
  margin-bottom: 0.25rem;
  color: ${({ theme }) => theme.colors.text.primary};
  font-weight: 600;
  line-height: 1.3;
`;

const SpeakerRole = styled.div`
  color: ${({ theme }) => theme.colors.primary.yellow};
  font-size: 0.85rem;
  font-weight: 500;
  margin-bottom: 1rem;
  line-height: 1.3;
`;

const SessionInfo = styled.div`
  background: rgba(0, 128, 214, 0.1);
  padding: 0.875rem;
  border-radius: 10px;
  border-left: 4px solid ${({ theme }) => theme.colors.primary.blue};
`;

const SessionTitle = styled.div`
  font-size: 0.85rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.primary.blue};
  margin-bottom: 0.25rem;
  line-height: 1.3;
`;

const SessionTime = styled.div`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.text.secondary};
  font-family: ${({ theme }) => theme.fonts.mono};
`;

const CTASection = styled.div`
  text-align: center;
  margin-top: 4rem;
  padding: 2rem;
  background: ${({ theme }) => theme.colors.surface};
  border-radius: 20px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  position: relative;
  z-index: 2;
`;

const Button = styled.button`
  padding: 1rem 2rem;
  border: 2px solid ${({ theme }) => theme.colors.border};
  border-radius: 25px;
  background: transparent;
  color: ${({ theme }) => theme.colors.text.secondary};
  cursor: not-allowed;
  opacity: 0.6;
  font-weight: 600;
`;

const Speakers = () => {
  const [activeDay, setActiveDay] = useState(1);

  const speakersData = {
  1: [
    {
      id: 'mari-galloway',
      name: 'Mari Galloway',
      role: 'Keynote Speaker',
      company: '',
      imageFile: 'Mari_Galloway.png',
      icon: '🎤',
      session: { title: 'Keynote', time: 'Day 1 • 10:00 AM' }
    },
    {
      id: 'mccasland',
      name: 'Connar McCasland',
      role: 'Instructor, Center for Cybersecurity, University of West Florida',
      company: 'University of West Florida',
      imageFile: 'Connar_McCasland.jpg',
      icon: '🔍',
      session: { title: 'Your Secrets Are Showing: What Hackers Can Find Online', time: 'Day 1 • 10:45 AM' }
    },
    {
      id: 'zubizarreta',
      name: 'Denise Zubizarreta',
      role: 'M.A.L.C.M. - Cultural Strategist Protecting Creative Ecosystems',
      company: 'Creative Ecosystems Protection',
      imageFile: 'Denise_Zubizarreta.jpg',
      icon: '🎨',
      session: { title: 'Cultural Firewalls: Bridging the Gap Between Nonprofits, Arts Organizations, and Cybersecurity', time: 'Day 1 • 11:15 AM' }
    },
    {
      id: 'muntz',
      name: 'Ethan Muntz',
      role: 'Healthcare Security Expert',
      company: '',
      imageFile: 'Ethan Muntz.jpeg',
      icon: '🏥',
      session: { title: 'Current and Emerging Threat Trends in the Health Sector', time: 'Day 1 • 11:30 AM' }
    },
    // {
    //   id: 'kc7-team',
    //   name: 'KC7 Team',
    //   role: 'Cyber Detection Game Facilitators',
    //   company: 'KC7',
    //   imageFile: 'KC7_Team.png',
    //   icon: '🎮',
    //   session: { title: 'KC7: Cyber Detection Game', time: 'Day 1 • 11:30 AM' }
    // },
    {
      id: 'wellnitz',
      name: 'Jacob Wellnitz',
      role: 'Senior Security Consultant, Beazley Security',
      company: 'Beazley Security',
      imageFile: 'Jacob_Wellnitz.jpg',
      icon: '💰',
      session: { title: 'The Human Cost of Cyber Attacks: Beyond the Financial Impacts', time: 'Day 1 • 11:45 AM' }
    },
    {
      id: 'sanders-panel',
      name: 'Kristen Sanders',
      role: 'CISO/Leadership Panel Moderator',
      company: '',
      imageFile: 'Kristen_Sanders.jpg',
      icon: '👥',
      session: { title: 'CISO/Leadership Panel', time: 'Day 1 • 1:00 PM' }
    },
    {
      id: 'callaway',
      name: 'Jack Callaway',
      role: 'Threat Detection Operations Lead',
      company: 'Kaiser Permanente',
      imageFile: 'Jack_Callaway.jpg',
      icon: '🔍',
      session: { title: 'From Theory to Practice: Building and Assessing Blue Team Playbooks', time: 'Day 1 • 1:00 PM' }
    },
    {
      id: 'orgeron',
      name: 'Mark Orgeron',
      role: 'Cybersecurity Ethics Expert',
      company: '',
      imageFile: 'Mark Orgeron.jpeg',
      icon: '⚖️',
      session: { title: 'Just because you can, doesn\'t mean you should!', time: 'Day 1 • 1:45 PM' }
    },
    {
      id: 'phillip',
      name: 'Phillip Smith',
      role: 'Healthcare Practice Leader & Vice President',
      company: '',
      imageFile: 'Phillip_Smith.jpeg',
      icon: '🏥',
      session: { title: 'Your Extended Team when calling a code', time: 'Day 1 • 1:45 PM' }
    },
    {
      id: 'mckean',
      name: 'Kadi McKean',
      role: 'Empowering the Possible',
      company: 'Empowering the Possible',
      imageFile: 'Kadi_McKean.jpg',
      icon: '🔗',
      session: { title: 'Trailblazing: Lessons from Oregon Trail for the Secure Software Supply Chain', time: 'Day 1 • 2:45 PM' }
    },
    {
      id: 'warren',
      name: 'Raphael Warren',
      role: 'Cybersecurity Specialist',
      company: '',
      imageFile: 'Raphael Warren.png',
      icon: '👻',
      session: { title: 'Unwanted Cyber Guest', time: 'Day 1 • 2:45 PM' }
    },
    {
      id: 'crawford',
      name: 'J. B. Crawford',
      role: 'Sr Professional Services Engineer, GitLab',
      company: 'GitLab',
      imageFile: 'J._B._Crawford.jpg',
      icon: '🔄',
      session: { title: 'Continuous Integration, Continuous Exfiltration', time: 'Day 1 • 2:45 PM' }
    },
    {
      id: 'jensen',
      name: 'Lee Jensen',
      role: 'AI Security Specialist',
      company: '',
      imageFile: 'Lee Jensen.png',
      icon: '🧠',
      session: { title: 'Enhancing Cybersecurity Intelligence with Retrieval-Augmented Generation (RAG)', time: 'Day 1 • 3:15 PM' }
    },
    {
      id: 'barker',
      name: 'Christina Barker',
      role: 'Senior Manager, Global Cyber Defense, Celonis, Inc',
      company: 'Celonis, Inc',
      imageFile: 'Christina_Barker.jpg',
      icon: '⚡',
      session: { title: 'Chaos Navigation: Mastering Preparedness for Success in Incident Response', time: 'Day 1 • 3:30 PM' }
    },
    {
      id: 'khichi',
      name: 'Shubham Khichi',
      role: 'Security Researcher',
      company: '',
      imageFile: 'Shubham Khichi.png',
      icon: '🔬',
      session: { title: 'How I found a 0 day using Inversion Theory', time: 'Day 1 • 3:30 PM' }
    },
    {
      id: 'edington',
      name: 'Dy Edington',
      role: 'Director, Information Security, BlueHalo',
      company: 'BlueHalo',
      imageFile: 'Dy_Edington.jpg',
      icon: '🛡️',
      session: { title: 'CMMC - Not an "IT" Certification', time: 'Day 1 • 3:45 PM' }
    },
    {
      id: 'sanders',
      name: 'Kristen Sanders',
      role: 'Cyber Defense Leader',
      company: '',
      imageFile: 'Kristen_Sanders.jpg',
      icon: '🦸‍♀️',
      session: { title: 'Cyber Defenders: Turning Skills Into Lifesaving Action', time: 'Day 1 • 4:30 PM' }
    },
    {
      id: 'kreofsky',
      name: 'Laura Kreofsky',
      role: 'Healthcare AI Security Expert',
      company: '',
      imageFile: 'Laura Kreofsky.png',
      icon: '🏥',
      session: { title: 'Building Rural Health Resiliency with Cybersecurity and AI', time: 'Day 1 • 4:30 PM' }
    }
  ],
  2: [
    {
      id: 'fidel',
      name: 'Mark Fidel',
      role: 'Keynote Speaker',
      company: '',
      imageFile: 'Mark Fidel.jpeg',
      icon: '🎤',
      session: { title: 'Keynote', time: 'Day 2 • 9:00 AM' }
    },
    {
      id: 'turner',
      name: 'Micah Turner',
      role: 'Cybersecurity Professional, Desert Research Institute',
      company: 'Desert Research Institute',
      imageFile: 'Micah_Turner.jpg',
      icon: '🧠',
      session: { title: 'Prompt Engineering and Injection: Fundamental and Advanced Techniques', time: 'Day 2 • 9:45 AM' }
    },
    {
      id: 'abumuslim',
      name: 'Mohamed AbuMuslim',
      role: 'AI Security Researcher',
      company: '',
      imageFile: 'Mohamed AbuMuslim.png',
      icon: '🤖',
      session: { title: 'Attacking LLM-Powered Email Summarizers', time: 'Day 2 • 9:45 AM' }
    },
    {
      id: 'johnson',
      name: 'Jay Johnson',
      role: 'CTO, DER Security',
      company: 'DER Security',
      imageFile: 'Jay_Johnson.jpg',
      icon: '⚡',
      session: { title: 'Vulnerabilities and Defenses for Distributed Energy and EV Charging', time: 'Day 2 • 10:45 AM' }
    },
    {
      id: 'gough',
      name: 'Michael Gough',
      role: 'Malware Archaeology Expert, Blue Teamer, Incident Responder, and Threat Hunter',
      company: 'Blue Team & Incident Response',
      imageFile: 'Michael_Gough.jpg',
      icon: '🔬',
      session: { title: 'SecTopRat / Arechclient2 – The latest on this popular malware family | Hacking a backup power solution(s) for your home, Tornado tested!', time: 'Day 2 • 10:45 AM • 11:30 AM' }
    },
    {
      id: 'adams',
      name: 'Alex Adams',
      role: 'Attack Research & Penetration Tester',
      company: 'Independent',
      imageFile: 'Alex_Adams.jpg',
      icon: '🌪️',
      session: { title: 'Resisting Censorship Attacks from a Nation-State Threat', time: 'Day 2 • 11:15 AM' }
    },
    {
      id: 'gritzo',
      name: 'Russ Gritzo',
      role: 'Cyber Security Program Manager',
      company: 'Independent',
      imageFile: 'Russ_Gritzo.png',
      icon: '🛤️',
      session: { title: 'Pathways to Pentesting – thoughts on becoming a professional penetration tester', time: 'Day 2 • 11:45 AM' }
    },
    {
      id: 'gadient',
      name: 'Austin Gadient',
      role: 'Vail Cyber CTO & Co-Founder',
      company: 'Vail Cyber',
      imageFile: 'Austin_Gadient.jpg',
      icon: '🏔️',
      session: { title: 'Navigating the Virtualization Battlefield: A Deep Dive into Hypervisor Attack Vectors', time: 'Day 2 • 1:00 PM' }
    },
    {
      id: 'rosa',
      name: 'Christopher De La Rosa',
      role: 'Target Senior Cyber Fraud Intelligence Analyst',
      company: 'Target',
      imageFile: 'Christopher_De_La_Rosa.jpg',
      icon: '🕵️',
      session: { title: 'Deep Dive OSINT: Techniques for Finding Hidden Data', time: 'Day 2 • 1:00 PM' }
    },
    {
      id: 'rutherford',
      name: 'Rebecca Rutherford',
      role: 'Cyber Security Technical Staff, LANL, LA Daily Post',
      company: 'LANL, LA Daily Post',
      imageFile: 'Rebecca_Rutherford.jpg',
      icon: '🦸‍♀️',
      session: { title: 'Maximum Effort: How to be a Cybersecurity Superhero', time: 'Day 2 • 1:30 PM' }
    },
    {
      id: 'manrod',
      name: 'Mike Manrod',
      role: 'CISO, GCE',
      company: 'GCE',
      imageFile: 'Mike_Manrod.png',
      icon: '🔧',
      session: { title: 'The Rise of Remote Management Tool Abuse: Introducing the AutoRMM Testing Framework', time: 'Day 2 • 2:00 PM' }
    },
    {
      id: 'woods',
      name: 'Ezra Woods',
      role: 'Security Engineer',
      company: 'GCE',
      imageFile: 'Ezra_Woods.jpg',
      icon: '🛠️',
      session: { title: 'The Rise of Remote Management Tool Abuse: Introducing the AutoRMM Testing Framework', time: 'Day 2 • 2:00 PM' }
    },
    {
      id: 'mccasland-2',
      name: 'Connar McCasland',
      role: 'Instructor, Center for Cybersecurity, University of West Florida',
      company: 'University of West Florida',
      imageFile: 'Connar_McCasland.jpg',
      icon: '🎓',
      session: { title: 'Chart Your Own Path: Navigating Cybersecurity Certifications', time: 'Day 2 • 2:40 PM' }
    },
    {
      id: 'ashley',
      name: 'Ashley',
      role: 'Special Guest Speaker',
      company: '',
      imageFile: 'Ashley.jpeg',
      icon: '⭐',
      session: { title: 'Ashley Talk', time: 'Day 2 • 3:10 PM' }
    }
  ]
};

  // Helper function to get image path
  const getImagePath = (imageFile) => {
    return imageFile ? `/speakers/${imageFile}` : null;
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.1
      }
    },
    exit: {
      opacity: 0,
      transition: {
        staggerChildren: 0.05,
        staggerDirection: -1
      }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 30, scale: 0.9 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    },
    exit: {
      opacity: 0,
      y: -30,
      scale: 0.9,
      transition: {
        duration: 0.4
      }
    }
  };

  // Animated background elements
  const backgroundElements = [
    // Floating circles
    ...Array.from({ length: 6 }, (_, i) => (
      <FloatingElement
        key={`circle-${i}`}
        size={40 + Math.random() * 60}
        color={i % 2 === 0 ? '#0080D630' : '#FFE50030'}
        initial={{ 
          x: Math.random() * (typeof window !== 'undefined' ? window.innerWidth : 1200),
          y: Math.random() * 600,
          opacity: 0 
        }}
        animate={{
          x: [null, Math.random() * (typeof window !== 'undefined' ? window.innerWidth : 1200)],
          y: [null, Math.random() * 600],
          opacity: [0, 0.3, 0]
        }}
        transition={{
          duration: 20 + Math.random() * 10,
          repeat: Infinity,
          repeatType: "reverse",
          ease: "easeInOut"
        }}
        style={{
          left: `${Math.random() * 100}%`,
          top: `${Math.random() * 100}%`
        }}
      />
    )),
    
    // Network nodes
    ...Array.from({ length: 8 }, (_, i) => (
      <NetworkNode
        key={`node-${i}`}
        rotation={Math.random() * 360}
        initial={{ 
          scale: 0,
          opacity: 0
        }}
        animate={{
          scale: [0, 1, 0],
          opacity: [0, 0.6, 0]
        }}
        transition={{
          duration: 8 + Math.random() * 4,
          repeat: Infinity,
          delay: i * 1.5,
          ease: "easeInOut"
        }}
        style={{
          left: `${20 + Math.random() * 60}%`,
          top: `${20 + Math.random() * 60}%`
        }}
      />
    )),
    
    // Code particles
    ...Array.from({ length: 5 }, (_, i) => (
      <CodeParticle
        key={`code-${i}`}
        initial={{ 
          x: '-10%',
          y: Math.random() * 600,
          opacity: 0
        }}
        animate={{
          x: '110%',
          opacity: [0, 0.5, 0]
        }}
        transition={{
          duration: 15 + Math.random() * 10,
          repeat: Infinity,
          delay: i * 3,
          ease: "linear"
        }}
        style={{
          top: `${20 + i * 15}%`
        }}
      >
        {`// speaker_${i + 1}.init() -> security.level = "expert"`}
      </CodeParticle>
    ))
  ];

  return (
    <SpeakersContainer id="speakers">
      <AnimatedBackground>
        {backgroundElements}
      </AnimatedBackground>
      
      <div className="container">
        <SpeakersHeader>
          <SpeakersTitle
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            Featured Speakers
          </SpeakersTitle>
          
          <SpeakersSubtitle>
            World-class cybersecurity experts sharing their knowledge and experience
          </SpeakersSubtitle>
          
          <AnnouncementBanner
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            viewport={{ once: true }}
          >
            <strong style={{ color: '#FFE500' }}>🎯 Join us July 25-26 for expert insights!</strong>
          </AnnouncementBanner>
        </SpeakersHeader>
        
        <DayTabs>
          <DayTab
            active={activeDay === 1}
            onClick={() => setActiveDay(1)}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Day 1 - July 25
          </DayTab>
          <DayTab
            active={activeDay === 2}
            onClick={() => setActiveDay(2)}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Day 2 - July 26
          </DayTab>
        </DayTabs>
        
        <SpeakersContent>
          <AnimatePresence mode="wait">
            <SpeakersGrid
              key={activeDay}
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
            >
              {speakersData[activeDay].map((speaker, index) => (
                <SpeakerCard
                  key={`${activeDay}-${speaker.id}`}
                  variants={cardVariants}
                  whileHover={{ scale: 1.02 }}
                  layout
                >
                  <SpeakerImage hasImage={!!speaker.imageFile}>
                    {speaker.imageFile ? (
                      <img 
                        src={getImagePath(speaker.imageFile)} 
                        alt={speaker.name}
                        onError={(e) => {
                          // Safely handle image load errors
                          if (e.target && e.target.parentNode) {
                            e.target.style.display = 'none';
                            const container = e.target.parentNode;
                            container.innerHTML = `<span style="font-size: 3rem;">${speaker.icon}</span>`;
                            container.style.background = 'linear-gradient(135deg, #0080D6, #FFE500)';
                          }
                        }}
                      />
                    ) : (
                      <span style={{ fontSize: '3rem' }}>{speaker.icon}</span>
                    )}
                  </SpeakerImage>
                  <SpeakerInfo>
                    <SpeakerName>{speaker.name}</SpeakerName>
                    <SpeakerRole>{speaker.role}</SpeakerRole>
                    <SessionInfo>
                      <SessionTitle>{speaker.session.title}</SessionTitle>
                      <SessionTime>{speaker.session.time}</SessionTime>
                    </SessionInfo>
                  </SpeakerInfo>
                </SpeakerCard>
              ))}
            </SpeakersGrid>
          </AnimatePresence>
        </SpeakersContent>
        
        <CTASection>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h3 style={{ color: '#FFE500', marginBottom: '1rem', fontSize: '1.5rem' }}>
              Want to speak at Security BSides Albuquerque?
            </h3>
            <p style={{ color: '#B0C4DE', marginBottom: '2rem' }}>
              Share your cybersecurity expertise with our community
            </p>
            <Button variant="ghost" size="large" disabled>
              🚫 Submissions Closed
            </Button>
          </motion.div>
        </CTASection>
      </div>
    </SpeakersContainer>
  );
};

export default Speakers;